import { type ComputedRef, type MaybeRefOrGetter, type Ref, isRef, ref, unref, watch } from "vue";

// Overload for when replace is provided and is a function
export default function usePropsAsDefault<T, U>(payload: {
  dynamicProps: ComputedRef<T>;
  replace: (dynamicProps: T) => U;
}): Ref<U>;

// Overload for when replace is provided and is not a function (MaybeRefOrGetter<U>)
export default function usePropsAsDefault<T, U>(payload: {
  dynamicProps: ComputedRef<T>;
  replace: U;
}): Ref<T>;

// Overload for when replace is not provided
export default function usePropsAsDefault<T>(payload: {
  dynamicProps: ComputedRef<T>;
  replace?: never;
}): Ref<T>;

// Implementation
export default function usePropsAsDefault<T, U>(payload: {
  dynamicProps: ComputedRef<T>;
  replace?: MaybeRefOrGetter<U> | ((dynamicProps: T) => U);
}): Ref<T> | Ref<U> {
  const props = ref<T | U>() as Ref<T | U>;

  function setValue() {
    if (typeof payload.replace === "function" && !isRef(payload.replace)) {
      props.value = (payload.replace as (dynamicProps: T) => U)(payload.dynamicProps.value);
    } else {
      props.value = payload.dynamicProps.value;
    }
  }

  setValue();

  watch(payload.dynamicProps, () => {
    if (
      (typeof payload.replace === "function" && !isRef(payload.replace)) ||
      unref(payload.replace)
    ) {
      setValue();
    }
  });

  return props as Ref<T> | Ref<U>;
}
